import { Box, styled, Typography } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import { JobType } from 'shared/components/filters/inputs/job-types/filter-job-types.types';
import { useGetShiftClaimExport } from 'ogp/hooks/queryHooks/shiftclaims/useGetShiftClaimExport';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { shiftAlerts as alerts, useAlert } from 'shared/components/alerts';
import { RenderUnsettledUI } from 'shared/components/render-unsettled-ui';
import { LwButton, LwFormDate, LwFormMultiSelect, LwIconButton } from 'redesign';
import { IconCross } from 'assets/img';
import { useGetOgpJobTypes } from 'ogp/hooks/query/filters/use-get-job-types';
import { usePlanningDateRange } from '../../hooks/use-get-path-helpers';

type ExportFilters = {
  fromDate: string;
  toDate: string;
  jobTypes: JobType[];
};

type ExportModalProps = {
  exportModalOpen: boolean;
  setExportModalOpen: (open: boolean) => void;
};

export const ExportModal = ({ exportModalOpen, setExportModalOpen }: ExportModalProps) => {
  const { startDate, endDate } = usePlanningDateRange();

  const theme = useTheme();
  const { alertSuccess, alertError } = useAlert();
  const { handleSubmit, watch, control, setValue, reset } = useForm<ExportFilters>();
  const jobTypeQuery = useGetOgpJobTypes();

  const today = new Date().toISOString().split('T')[0];
  const mutation = useGetShiftClaimExport({
    onSuccess: () => alertSuccess(alerts.success.downloadExport),
    onError: (e) => alertError(e),
  });

  useEffect(() => {
    setValue('fromDate', startDate.toFormat('yyyy-LL-dd'));
    setValue('toDate', endDate.toFormat('yyyy-LL-dd'));
  }, [exportModalOpen, endDate, endDate, setValue]);

  const disabled = !(watch('fromDate') && watch('toDate') && watch('jobTypes')?.length);

  function onSubmit(value: ExportFilters) {
    const { fromDate, toDate, jobTypes } = value;
    const filterData = {
      fromDate,
      toDate,
      jobTypeIds: jobTypes.map((jobType) => jobType.id),
    };

    mutation.mutate(filterData);
  }

  function clearForm() {
    reset();
  }

  if (jobTypeQuery.status !== 'success') {
    return <RenderUnsettledUI data={jobTypeQuery} />;
  }

  return (
    <Drawer
      anchor="right"
      open={exportModalOpen}
      onClose={() => setExportModalOpen(false)}
      ModalProps={{ style: { zIndex: theme.zIndex.appBar - 1 } }}
    >
      <StyledDrawerContent>
        <StyledDrawerHead>
          <Typography variant="h3">Exporteer shifts</Typography>
          <LwIconButton
            iconColorMode="stroke"
            color="secondary"
            onClick={() => setExportModalOpen(false)}
          >
            <IconCross />
          </LwIconButton>
        </StyledDrawerHead>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <StyledFormFields>
            <StyledFormDates>
              <LwFormDate
                name="fromDate"
                label="Vanaf"
                control={control}
                maxDate={watch('toDate') || today}
              />
              <LwFormDate name="toDate" label="Tot" control={control} minDate={watch('fromDate')} />
            </StyledFormDates>
            <LwFormMultiSelect
              control={control}
              name="jobTypes"
              ariaLabelInput={'jobType'}
              options={jobTypeQuery.data.jobTypes}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
              onChange={(_, selectedOptions) => {
                setValue('jobTypes', selectedOptions);
              }}
              isOptionEqualToValue={(option, value) => {
                return option?.id === value.id;
              }}
              label="Functies"
              enableSelectAll={true}
            />
          </StyledFormFields>
          <StyledActions>
            <LwButton color="primary" type="submit" disabled={disabled}>
              Download
            </LwButton>
            <LwButton color="secondary" onClick={clearForm}>
              Wissen
            </LwButton>
          </StyledActions>
        </StyledForm>
      </StyledDrawerContent>
    </Drawer>
  );
};

const StyledDrawerContent = styled(Box)(({ theme }) => ({
  width: theme.spacing(100),
  height: '100%',
}));

const StyledDrawerHead = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(8, 8, 6),
}));

const StyledForm = styled('form')(() => ({
  height: 'calc(100% - 112px)', // head height + margin bottom
}));

const StyledFormFields = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 8, 2, 8),
  height: 'calc(100% - 105px)', // form actions height
  overflowY: 'scroll',
}));

const StyledFormDates = styled(Box)(() => ({
  display: 'flex',
  gap: '14px',
}));

const StyledActions = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(4, 8, 8),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderTop: `1px solid ${theme.palette.grey[100]}`,
  background: 'white',
}));
